import React, { useState, useMemo, useEffect } from "react";
import { sizes } from "./sizes";
import axios from "axios";


function ReviewOrderScreen({ photos, quantities, setPhotos, setQuantities, onBack, onEditPhoto, photoOverlays, setPhotoOverlays, orderMode, selectedShop, croppedImages = {}, setCroppedImages, }) {
    // Extra order options
    const [paperFinish, setPaperFinish] = useState("Lustre");
    const [photoBorder, setPhotoBorder] = useState("No Border");
    const [colorCorrection, setColorCorrection] = useState("No thanks");
    const [saveToUSB, setSaveToUSB] = useState("No");
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [cropPhoto, setCropPhoto] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    // Confirmation modal
    const [confirmDelete, setConfirmDelete] = useState(null);
    // Flatten category-based sizes array into a single array of size objects
    const allSizes = sizes.flatMap((cat) => cat.items || []);
    const [loadingPercent, setLoadingPercent] = useState(0);
    const [showAgreement, setShowAgreement] = useState(false);
    const [adjustedSubtotal, setAdjustedSubtotal] = useState(null);


    // ✅ WP REST API setup
    const apiRoot =
        typeof window !== "undefined" && window.wpApiSettings?.root
            ? window.wpApiSettings.root
            : "https://opticonframing.com.au/wp-json/";
    const apiNonce =
        typeof window !== "undefined" && window.wpApiSettings?.nonce
            ? window.wpApiSettings.nonce
            : "";
    // ✅ Dynamic subtotal calculation
    const subtotalData = useMemo(() => {
        let total = 0;

        // 🔹 Calculate total prints
        photos.forEach((p) => {
            allSizes.forEach((s) => {
                if (!s) return;
                const qty = quantities[p.name]?.[s.label] || 0;
                total += qty * s.price;
            });
        });

        // 🔹 Count total number of ordered prints
        const totalPhotosOrdered = photos.reduce(
            (sum, p) =>
                sum +
                Object.values(quantities[p.name] || {}).reduce((a, b) => a + b, 0),
            0
        );

        // 🔹 Calculate color correction cost
        let correctionRate = 0;
        let colorCorrectionTotal = 0;

        if (colorCorrection === "Yes please") {
            if (totalPhotosOrdered > 100) correctionRate = 0.25;
            else if (totalPhotosOrdered > 25) correctionRate = 0.5;
            else if (totalPhotosOrdered > 10) correctionRate = 1;
            else if (totalPhotosOrdered > 5) correctionRate = 1.5;
            else if (totalPhotosOrdered > 0) correctionRate = 2;

            colorCorrectionTotal = totalPhotosOrdered * correctionRate;
            total += colorCorrectionTotal;
        }

        return { total, totalPhotosOrdered, correctionRate, colorCorrectionTotal };
    }, [photos, quantities, colorCorrection]);


    const getFilterStyle = (filter) => {
        switch (filter) {
            case "blackwhite": return "grayscale(100%)";
            case "sepia": return "sepia(100%)";
            default: return "none";
        }
    };

    // ✅ Display photo + size combos
    const displayedPrints = photos.flatMap((photo) =>
        allSizes
            .filter((s) => s && (quantities[photo.name]?.[s.label] || 0) > 0)
            .map((s) => ({
                photo,
                size: s.label,
                qty: quantities[photo.name][s.label],
            }))
    );

    // Delete handler
    const handleDelete = (photoName, sizeToDelete = null) => {
        // This flag tracks if the *entire photo* should be removed from the order (all print sizes are now zero).
        // It is used to decide whether to delete cropped/overlay data in the subsequent steps.
        let shouldRemovePhoto = false;

        setQuantities((prev) => {
            const newQty = { ...prev };

            if (newQty[photoName]) {
                if (sizeToDelete) {
                    // *** FIX: Only remove the quantity for the selected size. ***
                    newQty[photoName][sizeToDelete] = 0;

                    // Check if any other sizes for this photo still have a quantity > 0
                    const hasRemainingQuantities = Object.values(newQty[photoName]).some(
                        (qty) => qty > 0
                    );

                    if (!hasRemainingQuantities) {
                        // If no prints are left across all sizes, delete the photo entry completely
                        delete newQty[photoName];
                        shouldRemovePhoto = true;
                    }
                } else {
                    // If 'sizeToDelete' is NOT provided, it means we are deleting the ENTIRE photo 
                    // (e.g., a "Delete All" button).
                    delete newQty[photoName];
                    shouldRemovePhoto = true;
                }
            }

            return newQty;
        });

        // Only remove cropped image and overlay if the photo was completely removed from the quantities state.
        if (shouldRemovePhoto) {
            // remove cropped image for this photo
            if (typeof setCroppedImages === "function") {
                setCroppedImages((prev) => {
                    const copy = { ...prev };
                    if (copy[photoName]) delete copy[photoName];
                    return copy;
                });
            }

            // remove overlay for this photo
            if (typeof setPhotoOverlays === "function") {
                setPhotoOverlays((prev) => {
                    const copy = { ...prev };
                    if (copy[photoName]) delete copy[photoName];
                    return copy;
                });
            }
        }

        setConfirmDelete(null);
    };

    // Filtered photos
    const displayedPhotos = photos.filter((photo) =>
        Object.values(quantities[photo.name] || {}).some((q) => q > 0)
    );

    // --- NEW LOGIC TO GET ID FROM URL ---
    const ProductID = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get("product_id");
        return id ? parseInt(id, 10) : null;
    }, []);


    const handleAddToCart = async (finalSubtotal) => {
        setLoading(true);
        setLoadingPercent(0);
        try {
            // Generate a unique key for this cart row (bulletproof)
            const uniqueRowKey = Date.now() + '_' + Math.random().toString(36).substring(2);

            const orderData = {
                photos: displayedPrints.map((item) => {
                    // ✅ Construct the unique key we created in PrintOrderScreen
                    const uniqueKey = `${item.photo.name}_${item.size}`;

                    return {
                        photo: item.photo.name,
                        // ✅ Check for the specific size crop first, fallback to original
                        url: croppedImages?.[uniqueKey] || item.photo.url,
                        size: item.size,
                        qty: item.qty,
                        unique_key: uniqueRowKey,
                    };
                }),
                options: { paperFinish, photoBorder, colorCorrection, comment, order_mode: orderMode, selected_shop: selectedShop, },
                subtotal: finalSubtotal,
                unique_key: uniqueRowKey,
            };

            const productId = ProductID;

            const response = await axios.post(
                `https://opticonframing.com.au/wp-json/photo-prints/v1/add-to-cart`,
                {
                    product_id: productId,
                    quantity: 1,
                    order_data: orderData,
                    image: null,
                },
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                    onUploadProgress: (progressEvent) => {
                        const percent = Math.round(
                            (progressEvent.loaded * 100) / (progressEvent.total || 1)
                        );
                        setLoadingPercent(percent);
                    },
                }
            );

            if (response.data.success) {
                console.log("✅ Added to Cart:", orderData);
                window.location.href = "https://opticonframing.com.au/cart/";
            } else {
                alert("Failed to add to cart: " + response.data.message);
            }
        } catch (err) {
            console.error("❌ Error adding to cart:", err);
            alert("Failed to add to cart. Check console for details.");
        } finally {
            setLoading(false);
            setLoadingPercent(0);
            setShowAgreement(false); // hide popup
        }
    };

    const handleAddToCartClick = () => {
        if (orderMode === "online" && subtotalData.total < 5) {
            setShowAgreement(true);
        } else {
            handleAddToCart(subtotalData.total);
        }
    };

    return (
        <div className="review-order-container">
            {/* Top bar */}
            <header className="review-topbar">
                <button className="back-edit-btn" onClick={onBack}>
                    <i className="fa-solid fa-arrow-left"></i> &nbsp; Back to Edit Order
                </button>
                <button className="cart-btn" onClick={handleAddToCartClick}>
                    Add to Cart &nbsp; <i className="fa-solid fa-arrow-right"></i>
                </button>
            </header>

            <div className="review-content">
                {/* LEFT SIDE: Photos */}
                <div className="review-photos">
                    {displayedPrints.map((item, index) => {
                        const overlay = photoOverlays[item.photo.name];
                        const uniqueKey = `${item.photo.name}_${item.size}`;
                        const displayImage = croppedImages?.[uniqueKey] || item.photo.url;
                        // ✅ compute overlay position relative to image box if normalizedFrame exists
                        let overlayStyle = null;
                        if (overlay?.normalizedFrame && overlay?.baseImageBox) {
                            const box = overlay.baseImageBox;
                            const nf = overlay.normalizedFrame;
                            const actualFrame = {
                                width: nf.width * box.width,
                                height: nf.height * box.height,
                                top: nf.top * box.height + box.top,
                                left: nf.left * box.width + box.left,
                            };
                            overlayStyle = {
                                position: "absolute",
                                border: "2px dashed red",
                                pointerEvents: "none",
                                width: `${actualFrame.width}px`,
                                height: `${actualFrame.height}px`,
                                top: `${actualFrame.top}px`,
                                left: `${actualFrame.left}px`,
                                transform: `rotate(${overlay.rotation || 0}deg)`,
                            };
                        }
                        return (
                            <div className="photo-card" key={item.photo.name + item.size}>
                                <div
                                    className="image-wrapper"
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        width: "100%",
                                        aspectRatio: "4 / 3", // ✅ keeps image cards uniform
                                        overflow: "hidden",
                                        borderRadius: "10px",
                                    }}
                                >
                                    <img
                                        src={displayImage}
                                        alt={item.photo.name}
                                        className="review-image"
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain", // maintains aspect ratio inside card
                                            borderRadius: "8px",
                                            backgroundColor: "#f5f5f5", // optional nice neutral background
                                            filter: getFilterStyle(item.photo.filterApplied),
                                        }}
                                    />
                                    {overlayStyle && <div className="crop-overlay" style={overlayStyle} />}
                                </div>

                                <div className="photo-info">
                                    <p>{item.size}</p>
                                </div>
                                <div className="photo-footer">
                                    <span>Qty: {item.qty}</span>
                                    <span>
                                        Print: {index + 1}/{displayedPrints.length}
                                    </span>
                                    <span className="photo-actions">
                                        <button className="icon-btn" onClick={() => onEditPhoto(item.photo)}>
                                            <i className="fa-solid fa-pen"></i>
                                        </button>
                                        <button
                                            className="icon-btn"
                                            onClick={() => setConfirmDelete({ photoName: item.photo.name, size: item.size })} // ✅ FIX 1: Pass an object with both keys
                                        >
                                            <i className="fa-solid fa-trash"></i>
                                        </button>
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* RIGHT SIDE: Order Summary */}
                <aside className="order-summary">
                    <h3>Order Review</h3>
                    <p>Number of photos: {photos.length}</p>
                    <p>
                        Number of prints:{" "}
                        {photos.reduce(
                            (sum, p) =>
                                sum +
                                Object.values(quantities[p.name] || {}).reduce((a, b) => a + b, 0),
                            0
                        )}
                    </p>

                    {/* Options */}
                    <div className="option-group">
                        <hr style={{ marginTop: "15px" }} />
                        <p>Paper Finish</p>
                        <label>
                            <input
                                type="radio"
                                value="Glossy"
                                checked={paperFinish === "Glossy"}
                                onChange={(e) => setPaperFinish(e.target.value)}
                                className="check-box"
                            />
                            Glossy
                        </label>
                        <br /><br />
                        <label>
                            <input
                                type="radio"
                                value="Lustre"
                                checked={paperFinish === "Lustre"}
                                onChange={(e) => setPaperFinish(e.target.value)}
                                className="check-box"
                            />
                            Lustre
                        </label>
                    </div>


                    <div className="option-group">
                        <hr />
                        <p>Photo Border</p>
                        <label>
                            <input
                                type="radio"
                                value="White Border"
                                checked={photoBorder === "White Border"}
                                onChange={(e) => setPhotoBorder(e.target.value)}
                                className="check-box"
                            />
                            White Border
                        </label>
                        <br />
                        <br />
                        <label>
                            <input
                                type="radio"
                                value="No Border"
                                checked={photoBorder === "No Border"}
                                onChange={(e) => setPhotoBorder(e.target.value)}
                                className="check-box"
                            />
                            No Border
                        </label>
                    </div>

                    <div className="option-group">
                        <hr />
                        <p>Color Correction</p>
                        <label>
                            <input
                                type="radio"
                                value="Yes please"
                                checked={colorCorrection === "Yes please"}
                                onChange={(e) => setColorCorrection(e.target.value)}
                                className="check-box"
                            />
                            Yes please
                        </label>
                        <br />
                        <br />
                        <label>
                            <input
                                type="radio"
                                value="No thanks"
                                checked={colorCorrection === "No thanks"}
                                onChange={(e) => setColorCorrection(e.target.value)}
                                className="check-box"
                            />
                            No thanks
                        </label>
                    </div>

                    <hr />
                    <br />
                    <br />

                    {/* Comment */}
                    <div className="textarea-wrapper">
                        <textarea
                            placeholder=" "
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="textarea"
                        />
                        <label className="textarea-label">Add a comment to this order</label>
                    </div>

                    {/* Subtotal */}
                    <div className="subtotal">
                        <p>Subtotal</p>
                        <p className="price">${subtotalData.total.toFixed(2)}</p>
                    </div>

                    {/* Show color correction breakdown if applied */}
                    {colorCorrection === "Yes please" && subtotalData.colorCorrectionTotal > 0 && (
                        <div className="color-correction-breakdown">
                            <p style={{ fontSize: "0.9rem", color: "#555" }}>
                                Color Correction ({subtotalData.totalPhotosOrdered} photos @ ${subtotalData.correctionRate.toFixed(2)})
                            </p>
                            <p style={{ fontSize: "0.9rem", color: "#555", textAlign: "right" }}>
                                +${subtotalData.colorCorrectionTotal.toFixed(2)}
                            </p>
                        </div>
                    )}

                    {/* Add to Cart button */}
                    <button className="add-to-cart-btn" onClick={handleAddToCartClick}>
                        Add to Cart
                    </button>



                </aside>
            </div>



            {/* Confirm Delete Modal */}
            {confirmDelete && (
                <div className="confirm-overlay">
                    <div className="confirm-box">
                        {/* ✅ FIX 2: Better modal text for clarity */}
                        <p>Are you sure you want to delete the <strong>{confirmDelete.size}</strong> print of this photo?</p>
                        <div className="confirm-actions">
                            <button className="cancel-btn" onClick={() => setConfirmDelete(null)}>
                                Cancel
                            </button>
                            <button
                                className="delete-btn"
                                // ✅ FIX 3: Correctly pass both arguments to handleDelete
                                onClick={() => handleDelete(confirmDelete.photoName, confirmDelete.size)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <div className="loading-percent">{loadingPercent}%</div>
                </div>
            )}

            {/* Minimum order agreement popup */}
            {showAgreement && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.3)", // semi-transparent background
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000, // ensures it's on top
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: "20px 30px",
                            borderRadius: "12px",
                            boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
                            maxWidth: "400px",
                            width: "90%",
                            textAlign: "center",
                        }}
                    >
                        <p>The minimum order is $5. Do you want to proceed?</p>
                        <div className="popup-actions" style={{ marginTop: "15px" }}>
                            <button
                                onClick={() => {
                                    handleAddToCart(5);
                                }}
                            >
                                Yes
                            </button>
                            <button
                                style={{ marginLeft: "10px" }}
                                onClick={() => setShowAgreement(false)}
                            >
                                No
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default ReviewOrderScreen;